import ImportController from 'cio/pods/imports/import/index/controller';
import { set, action, computed } from '@ember/object';
import { task, dropTask } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import { next } from '@ember/runloop';
import FeaturesService from 'ember-feature-flags/services/features';
import SegmentModel from 'cio/models/segment';
import FormValidations from 'cio/utils/form-validations';
import { hasNothingToImport } from 'cio/components/import-review/summary-message';
import { IdentifierKey, IdentifierState } from 'cio/utils/identifiers';
import { alias } from '@ember/object/computed';
import ImportModel from 'cio/models/import';
import { tracked } from '@glimmer/tracking';
import isDestroyed from 'cio/utils/is-destroyed';

export enum AddToSegmentType {
  None = 'none',
  Create = 'create',
  Update = 'update',
}

// If they already have a lot of manual segments, don't default to create a new one
const MANUAL_SEGMENT_CUTOFF = 100;

const Validations = {
  segmentName(this: ImportsImportSetupReviewController, value: string) {
    if (this.addToSegment === AddToSegmentType.Create && !value.length) {
      return 'Add a segment name';
    }

    return true;
  },
  segment(this: ImportsImportSetupReviewController, value: Record<string, string> | null) {
    if (this.addToSegment === AddToSegmentType.Update && !value) {
      return 'Select a segment to update';
    }

    return true;
  },
};

export default class ImportsImportSetupReviewController extends ImportController {
  @service features!: FeaturesService;

  declare model: ImportModel;

  readonly identifierKey = IdentifierKey;
  addToSegment: AddToSegmentType = AddToSegmentType.Create;
  @tracked formValues: Record<string, any> = {
    segmentName: '',
    segment: null,
  };
  validations: FormValidations;
  segmentOptionsDirty = false;
  @alias('model.validateMeta.errors') errors!: string[];

  constructor() {
    super(...arguments);

    this.validations = FormValidations.create({ validations: Validations, context: this, modelKey: 'formValues' });
  }

  @computed('model.{hasDirtyAttributes,updateType}', 'segmentOptionsDirty')
  get isDirty() {
    let { hasDirtyAttributes, updateType } = this.model;
    return ((hasDirtyAttributes as unknown as boolean) || this.segmentOptionsDirty) && updateType !== 'import';
  }

  @computed('model.hasNoProfilesToUpdate', 'hasWarnings')
  get showWarnings() {
    let { hasNoProfilesToUpdate, hasWarnings } = this.model;
    return hasWarnings && !hasNoProfilesToUpdate;
  }

  @computed('fetchSegments.last.value')
  get segmentOptions() {
    let fetchedSegments: SegmentModel[] = this.fetchSegments.last?.value ?? [];

    return fetchedSegments.map((segment) => ({
      label: segment.name,
      value: segment.id,
    }));
  }

  @computed('model.hasAllInvalidRows', 'model.validateMeta.{newProfiles,existingProfiles}')
  get hasNothingToImport() {
    return this.model.isEventsImport || hasNothingToImport(this.model, this.importType?.value ?? '');
  }

  // Only show ID Changes over Email changes if the update was by email and the ID identifier is
  // mutable in the workspace
  @computed('model.identifier')
  get showIdChanges() {
    return (
      this.model.identifier === IdentifierKey.Email &&
      this.environment.current.identifiers[IdentifierKey.Id] === IdentifierState.Enabled
    );
  }

  @computed('model.type')
  get segmentOptionsDisabled() {
    return this.model.segment.get('type') === 'static';
  }

  @computed('model.{validateMeta.newProfiles,newProfileCount}')
  get newProfileCount() {
    if (this.model.isEventsImport) {
      return this.model.validateMeta?.newProfiles ?? 0;
    }

    return this.model.newProfileCount;
  }

  importTask = task(async () => {
    try {
      await this.validations.validateModel();

      // Don't create segments when imported event type is not "people"
      if (!this.model.isPeopleImport) {
        this.set('addToSegment', AddToSegmentType.None);
      }

      this.model.set('updateType', this.addToSegment !== AddToSegmentType.None ? 'import_segment' : 'import');

      if (this.addToSegment === AddToSegmentType.Create) {
        this.model.set('segmentName', this.formValues.segmentName);
      } else if (this.addToSegment === AddToSegmentType.Update) {
        await this.model.setManualSegment.perform(this.formValues.segment.value);
      }

      await this.model.save();
      this.reset();

      this.router.transitionTo('imports.import.setup.importing', this.model.id);
    } catch (err) {
      // validation error, don't handle
      if (typeof err === 'boolean') {
        return;
      }

      this.model.rollbackAttributes();
      this.apiErrors.report(err);
    }
  });

  fetchSegments = dropTask(async () => {
    let result = await this.store.findAll('short-segment');
    let segments = result.toArray().filter((segment) => segment.type === 'static');

    if (segments.length > MANUAL_SEGMENT_CUTOFF) {
      this.set('addToSegment', AddToSegmentType.None);
    }

    return segments;
  });

  @action
  reset() {
    if (!isDestroyed(this.model)) {
      this.model.rollbackAttributes();
    }
    this.setProperties({
      addToSegment: AddToSegmentType.Create,
      formValues: {
        segmentName: '',
        segment: null,
      },
      segmentOptionsDirty: false,
    });
    this.validations.clearErrors();
  }

  @action
  handleSegmentChange(seg: unknown) {
    set(this.formValues, 'segment', seg);

    if (this.addToSegment !== AddToSegmentType.Update) {
      this.set('addToSegment', AddToSegmentType.Update);
    }

    // execute this on the next tick because the getter doesn't return the new value in time in the validations object
    next(this, () => {
      this.validations.validateAttribute('segment');
    });
  }

  @action
  async toggleAddToSegment(type: AddToSegmentType) {
    let toggleOff = this.addToSegment === type;

    if (type === AddToSegmentType.Create) {
      this.set('addToSegment', toggleOff ? AddToSegmentType.None : type);
    } else if (type === AddToSegmentType.Update) {
      this.set('addToSegment', toggleOff ? AddToSegmentType.None : type);
    }

    if (this.validations.isTouched) {
      try {
        await this.validations.validateModel();
      } catch {}
    }
  }

  @action
  segmentOptionsChanged() {
    this.set('segmentOptionsDirty', true);
  }

  @action
  onSegmentNameChange(segmentName: string) {
    this.formValues = {
      ...this.formValues,
      segmentName,
    };
    this.validations.validateAttribute('segmentName');
  }
}
